import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import type { TextStyle } from '@scribbleplot/shared';
import { shortcut } from '../ui/responsive';
import { typography, type ProseItem } from './typography';

export function TextFormatting({
  item,
  change,
  editor,
  onClose,
  floating = false,
  fontControl,
}: {
  item: ProseItem;
  change: (style: TextStyle) => void;
  editor: RefObject<HTMLTextAreaElement | null>;
  onClose: () => void;
  floating?: boolean;
  fontControl?: RefObject<HTMLSelectElement | null>;
}) {
  const rendered = typography(item);
  const [size, setSize] = useState(String(rendered.fontSize));
  useEffect(() => setSize(String(rendered.fontSize)), [rendered.fontSize]);
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ left: 12, top: 80 });
  const place = () => {
    if (!floating || !editor.current || !ref.current) return;
    const rect = editor.current.getBoundingClientRect();
    const bar = ref.current.getBoundingClientRect();
    const canvas = document.querySelector('[role="application"]')?.getBoundingClientRect();
    const minTop = (canvas?.top ?? 64) + 12;
    const maxTop = Math.max(minTop, innerHeight - bar.height - 12);
    let top = rect.top - bar.height - 12;
    if (top < minTop) top = rect.bottom + 12 <= maxTop ? rect.bottom + 12 : minTop;
    const next = {
      left: Math.max(12, Math.min(rect.left, innerWidth - bar.width - 12)),
      top: Math.max(minTop, Math.min(top, maxTop)),
    };
    setPosition((old) => (old.left === next.left && old.top === next.top ? old : next));
  };
  useLayoutEffect(place);
  useEffect(() => {
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  });
  const apply = (patch: TextStyle, restoreFocus = true) => {
    change({ ...item.textStyle, ...patch });
    if (restoreFocus) editor.current?.focus({ preventScroll: true });
  };
  const commitSize = () => {
    const value = Number(size);
    if (size.trim() && Number.isFinite(value) && value >= 8 && value <= 160) {
      if (value !== rendered.fontSize) apply({ fontSize: value }, false);
    } else setSize(String(rendered.fontSize));
  };
  const contents = (
    <div
      ref={ref}
      className={`sp-text-formatting${floating ? ' sp-text-formatting-floating' : ''}`}
      style={floating ? position : undefined}
      role="toolbar"
      aria-label="Text formatting"
      onPointerDown={(e) => e.stopPropagation()}
      onPointerMove={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Escape') {
          e.preventDefault();
          editor.current?.focus();
        }
      }}
    >
      <span className="sp-format-scope">Whole element</span>
      <label>
        Font
        <select
          ref={fontControl}
          aria-label="Font family"
          value={item.textStyle?.fontFamily ?? 'sans'}
          onChange={(e) => apply({ fontFamily: e.target.value as TextStyle['fontFamily'] })}
        >
          <option value="sans">Sans</option>
          <option value="serif">Serif</option>
          <option value="mono">Mono</option>
        </select>
      </label>
      <label>
        Size
        <input
          aria-label="Font size"
          type="number"
          min="8"
          max="160"
          value={size}
          onChange={(e) => setSize(e.target.value)}
          onBlur={commitSize}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitSize();
              editor.current?.focus();
            }
          }}
        />
      </label>
      <div className="sp-format-buttons">
        {(['bold', 'italic', 'underline'] as const).map((key, i) => {
          const label = key[0].toUpperCase() + key.slice(1);
          const active = key === 'bold' ? Number(rendered.fontWeight) >= 600 : !!item.textStyle?.[key];
          return (
            <button
              key={key}
              type="button"
              className={`sp-icon sp-format-${key}`}
              aria-label={label}
              aria-pressed={active}
              title={`${label} (${shortcut(['⌘B', '⌘I', '⌘U'][i])})`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => apply({ [key]: !active })}
            >
              {label[0]}
            </button>
          );
        })}
      </div>
      <label>
        Align
        <select
          aria-label="Text alignment"
          value={item.textStyle?.align ?? 'left'}
          onChange={(e) => apply({ align: e.target.value as TextStyle['align'] })}
        >
          <option value="left">Left</option>
          <option value="center">Center</option>
          <option value="right">Right</option>
        </select>
      </label>
      <label>
        Color
        <input
          aria-label="Text color"
          type="color"
          value={String(rendered.color)}
          onChange={(e) => apply({ color: e.target.value })}
        />
      </label>
      {floating && (
        <button type="button" className="sp-button" onClick={onClose}>
          Done
        </button>
      )}
    </div>
  );
  return floating ? createPortal(contents, document.body) : contents;
}

export function formatShortcut(event: React.KeyboardEvent, item: ProseItem, change: (style: TextStyle) => void) {
  if (!(event.metaKey || event.ctrlKey) || event.altKey) return;
  const key = ({ b: 'bold', i: 'italic', u: 'underline' } as const)[event.key.toLowerCase() as 'b' | 'i' | 'u'];
  if (!key) return;
  event.preventDefault();
  const active = key === 'bold' ? Number(typography(item).fontWeight) >= 600 : !!item.textStyle?.[key];
  change({ ...item.textStyle, [key]: !active });
}
